import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  UserPlusIcon as UserPlus,
  CircleNotchIcon as Spinner,
  WarningCircleIcon as WarningCircle,
} from "@phosphor-icons/react";
import { formatApiErrorDetail, useAuth } from "@/lib/auth";
import { CheckingFallback } from "./Login";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) navigate("/workspaces", { replace: true });
  }, [user, navigate]);

  if (user === undefined) return <CheckingFallback />;
  if (user) return <Navigate to="/workspaces" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await register(email, password, name);
      navigate("/workspaces", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 bg-[var(--ml-bg)]"
      data-testid="register-page"
    >
      <form
        onSubmit={onSubmit}
        className="ml-card w-full max-w-md p-8 space-y-5"
        data-testid="register-form"
      >
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <UserPlus size={11} weight="bold" />
            Create account
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Start your first engagement
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1">
            Workspaces, findings, and deliverables — all under your account.
          </p>
        </div>

        <Field label="Full name" type="text" autoComplete="name" value={name} onChange={setName} testid="register-name" required />
        <Field label="Email" type="email" autoComplete="email" value={email} onChange={setEmail} testid="register-email" required />
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          testid="register-password"
          required
          minLength={8}
          hint="Minimum 8 characters."
        />

        {error && (
          <div
            className="flex items-start gap-2 text-sm text-[#a2191f] bg-[#fff1f1] border border-[#ffd7d9] px-3 py-2"
            data-testid="register-error"
            role="alert"
          >
            <WarningCircle size={14} weight="bold" className="mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !email || !password || password.length < 8 || !name}
          data-testid="register-submit"
          className="w-full px-4 py-2 bg-[var(--ml-primary)] text-white font-semibold hover:bg-[var(--ml-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? <Spinner size={16} className="animate-spin" /> : null}
          {submitting ? "Creating…" : "Create account"}
        </button>

        <div className="text-sm text-[var(--ml-text-secondary)] text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-[var(--ml-primary)] hover:underline font-semibold" data-testid="register-to-login">
            Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, type, autoComplete, value, onChange, testid, required, minLength, hint }) {
  return (
    <label className="block">
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
        {label}
      </div>
      <input
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        minLength={minLength}
        data-testid={testid}
        className="w-full px-3 py-2 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)] text-sm"
      />
      {hint && (
        <div className="ml-mono text-[10px] text-[var(--ml-text-secondary)] mt-1">
          {hint}
        </div>
      )}
    </label>
  );
}
